import { T, Var, useGT } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { belowVendorMinQuantity } from './belowVendorMin';

// Warns that a buy sits under the vendor's master minimum order qty. The engine
// only goes there when a buyer pack-qty override replaces the master carton
// (GEN-2686); a hand-edited line quantity can too. Vendor-level PO minimums
// (MOV / freight) are unaffected and keep their own "below minimum" flag.
export function BelowVendorMinBadge({
  buyQuantity,
  vendorMoqQuantity,
  compact = false,
}: {
  buyQuantity: number | null | undefined;
  vendorMoqQuantity: number | null | undefined;
  /** Icon only (table cells); the full pill otherwise. */
  compact?: boolean;
}) {
  const gt = useGT();
  const moq = belowVendorMinQuantity(buyQuantity, vendorMoqQuantity);
  if (moq === null) return null;

  const label = gt('Below vendor min qty {moq}', {
    context: 'replenishment warning when a buy is under the vendor MOQ',
    moq: moq.toLocaleString(),
  });

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={label}
            className={
              compact
                ? 'inline-flex shrink-0 text-amber-600'
                : 'inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700'
            }
          >
            <WarningIcon className="h-3.5 w-3.5" aria-hidden />
            {compact ? null : label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <T context="replenishment below vendor MOQ tooltip">
            Below vendor min qty <Var>{moq.toLocaleString()}</Var> — a pack
            override or edited quantity is applied. Vendor PO minimums (MOV,
            freight) still apply.
          </T>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
